import React, { useState, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth, axiosInstance, API } from '../App';
import { ArrowLeft, Eye, EyeOff, ShieldAlert } from 'lucide-react';
import { toast } from 'sonner';
import LanguageToggle from '../components/LanguageToggle';
import axios from 'axios';
import EmailTakenNotice from '../components/EmailTakenNotice';

const SignUp = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { login } = useAuth();
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    password: '',
    confirmPassword: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [consentGiven, setConsentGiven] = useState(false);
  const [marketingConsent, setMarketingConsent] = useState(false);
  const [pendingInfo, setPendingInfo] = useState(null);

  // Iter 212 — Early email-availability gate.
  const [emailStatus, setEmailStatus] = useState('idle');
  const [emailTakenReason, setEmailTakenReason] = useState(null);
  const emailInputRef = useRef(null);
  const checkEmailAvailability = async (raw) => {
    const email = (raw || '').trim().toLowerCase();
    if (!email || !email.includes('@')) { setEmailStatus('idle'); setEmailTakenReason(null); return null; }
    setEmailStatus('checking');
    try {
      const r = await axios.get(`${API}/auth/check-email`, { params: { email } });
      const ok = !!r.data?.available;
      setEmailStatus(ok ? 'available' : 'taken');
      setEmailTakenReason(ok ? null : (r.data?.reason || 'user_exists'));
      return ok;
    } catch {
      setEmailStatus('idle');
      setEmailTakenReason(null);
      return null;
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!consentGiven) {
      setError(t('consentRequired', 'You must agree to the Terms & Conditions and Privacy Policy'));
      return;
    }

    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (formData.password.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }

    // Iter 212 — Block submission when the email is already registered.
    if (emailStatus !== 'available') {
      const ok = await checkEmailAvailability(formData.email);
      if (ok === false) {
        setError(t('emailAlreadyRegistered', 'This email address is already registered.'));
        return;
      }
    }

    setLoading(true);

    try {
      const response = await axiosInstance.post('/auth/register', {
        fullName: formData.fullName,
        email: formData.email,
        password: formData.password,
        role: 'user',
        consentGiven: consentGiven,
        marketingConsent: marketingConsent
      });
      
      const data = response.data;
      if (data?.pending) {
        setPendingInfo(data);
        return;
      }
      const { access_token, role, user_id } = data;
      // If registration succeeded but no token came back, the account exists —
      // route to sign in with a clear message rather than failing silently.
      if (!access_token) {
        navigate('/signin', {
          replace: true,
          state: {
            email: formData.email,
            notice: t(
              'autoLoginFailedNotice',
              'Your account was created successfully. Please sign in with the email and password you just chose.'
            ),
          },
        });
        return;
      }
      login(access_token, role, user_id);
      // Feb 2026 v6.1 — second touch of the informed-consent flow. The
      // toast is intentionally 10 s (not the default 4 s) so the user has
      // time to read it while the /discover landing renders, and provides
      // a direct link to the preference so they can act immediately.
      toast(
        t(
          'safetyEmailPostSignupToast',
          'Official Community Safety Alert emails are ON for your account. Change anytime in Notification Preferences.'
        ),
        {
          duration: 10000,
          icon: '🛡️',
          action: {
            label: t('manage', 'Manage'),
            onClick: () => navigate('/notifications'),
          },
        }
      );
      navigate('/');
    } catch (err) {
      setError(err.response?.data?.detail || 'Registration failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-purple-600 to-purple-800">
      <header className="p-4 flex justify-between items-center">
        <button onClick={() => navigate('/')} className="flex items-center text-white">
          <ArrowLeft className="h-6 w-6 mr-2" />
        </button>
        <LanguageToggle />
      </header>

      <div className="flex flex-col items-center justify-center px-6 py-8">
        {pendingInfo ? (
          <div className="w-full max-w-md bg-white rounded-2xl shadow-2xl p-8 text-center" data-testid="signup-pending-card">
            <div className="mx-auto mb-4 h-14 w-14 rounded-full bg-amber-100 flex items-center justify-center">
              <svg className="h-7 w-7 text-amber-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2"><path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
            </div>
            <h1 className="text-2xl font-bold text-gray-900 mb-2" data-testid="signup-pending-title">
              Account pending approval
            </h1>
            <p className="text-gray-600 mb-6" data-testid="signup-pending-message">
              {pendingInfo.message || "Your account has been created and is awaiting admin approval. You'll be able to sign in once it's approved."}
            </p>
            <Link to="/signin" className="inline-block w-full py-3 bg-purple-600 text-white font-semibold rounded-lg hover:bg-purple-700 transition-colors" data-testid="signup-pending-signin-link">
              {t('signIn')}
            </Link>
          </div>
        ) : (
        <div className="w-full max-w-md bg-white rounded-2xl shadow-2xl p-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2" data-testid="signup-title">
            {t('signUp')}
          </h1>
          <p className="text-gray-600 mb-8">{t('createAccount')}</p>

          {error && (
            <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('fullName')} *
              </label>
              <input
                type="text"
                placeholder="John Doe"
                value={formData.fullName}
                onChange={(e) => setFormData({ ...formData, fullName: e.target.value })}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 outline-none"
                required
                data-testid="fullname-input"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('email')} *
              </label>
              <input
                ref={emailInputRef}
                type="email"
                placeholder="your@email.com"
                value={formData.email}
                onChange={(e) => {
                  setFormData({ ...formData, email: e.target.value });
                  if (emailStatus !== 'idle') { setEmailStatus('idle'); setEmailTakenReason(null); }
                }}
                onBlur={(e) => checkEmailAvailability(e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 outline-none"
                required
                data-testid="email-input"
              />
              {emailStatus === 'checking' && (
                <p className="text-xs text-slate-500 mt-1" data-testid="user-email-checking">Checking…</p>
              )}
              {emailStatus === 'taken' && (
                <EmailTakenNotice
                  reason={emailTakenReason}
                  onChangeEmail={() => {
                    setFormData({ ...formData, email: '' });
                    setEmailStatus('idle');
                    setEmailTakenReason(null);
                    setTimeout(() => emailInputRef.current?.focus(), 0);
                  }}
                  testIdPrefix="user-email-taken"
                />
              )}
              {emailStatus === 'available' && (
                <p className="text-xs text-emerald-700 mt-1" data-testid="user-email-available">✓ Email available</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('password')} *
              </label>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  placeholder={t('auth.pwStrongPlaceholder', 'Create a strong password')}
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  className="w-full px-4 py-3 pe-10 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 outline-none"
                  required
                  data-testid="password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute end-3 top-1/2 -translate-y-1/2 text-gray-500"
                >
                  {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                </button>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
              {t('confirmPassword')} *
              </label>
              <input
                type="password"
                placeholder={t('auth.pwReenter', 'Re-enter password')}
                value={formData.confirmPassword}
                onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 outline-none"
                required
                data-testid="confirm-password-input"
              />
            </div>

            {/* Terms consent (required) */}
            <div className="flex items-start gap-3">
              <input
                type="checkbox"
                id="consent"
                checked={consentGiven}
                onChange={(e) => setConsentGiven(e.target.checked)}
                className="mt-1 h-4 w-4 text-purple-600 border-gray-300 rounded focus:ring-purple-500 cursor-pointer"
                data-testid="consent-checkbox"
              />
              <label htmlFor="consent" className="text-sm text-gray-600 cursor-pointer">
                {t('consentText', 'I agree to the')}{' '}
                <Link to="/terms" target="_blank" className="text-purple-600 hover:text-purple-700 underline font-medium">
                  {t('termsAndConditions', 'Terms & Conditions')}
                </Link>
                {', '}
                <Link to="/privacy" target="_blank" className="text-purple-600 hover:text-purple-700 underline font-medium">
                  {t('privacyPolicyTitle', 'Privacy Policy')}
                </Link>
                {' '}{t('consentAnd', 'and')}{' '}
                <Link to="/disputes" target="_blank" className="text-purple-600 hover:text-purple-700 underline font-medium">
                  {t('disputePolicyTitle', 'Dispute Policy')}
                </Link>
                {' *'}
              </label>
            </div>

            {/* Marketing consent (optional) */}
            <div className="flex items-start gap-3">
              <input
                type="checkbox"
                id="marketing-consent"
                checked={marketingConsent}
                onChange={(e) => setMarketingConsent(e.target.checked)}
                className="mt-1 h-4 w-4 text-purple-600 border-gray-300 rounded focus:ring-purple-500 cursor-pointer"
                data-testid="marketing-consent-checkbox"
              />
              <label htmlFor="marketing-consent" className="text-sm text-gray-500 cursor-pointer">
                {t('marketingConsentText', 'I agree to receive offers, events, and updates from businesses (optional)')}
              </label>
            </div>

            {/* Safety-alert email transparency notice (Feb 2026 v6.1) —
                Informed-consent surface for the criticalEmailBackup default.
                Users must be told at sign-up that they'll receive Official
                Community Safety Alert emails even if they never turn on any
                other email, and how to switch it off. This is the first of
                two touches — a toast confirms the same on the /discover
                landing after signup. */}
            <div
              className="flex items-start gap-3 p-3 bg-red-50/60 border border-red-200 rounded-lg"
              data-testid="safety-email-backup-notice"
            >
              <ShieldAlert className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
              <p className="text-[12px] leading-snug text-red-900">
                {t(
                  'safetyEmailNotice',
                  'To keep you safe, Email backup for Official Community Safety Alerts (floods, fires, road closures, police notices, town-hall warnings) is ON by default. It does not affect your marketing choice above. You can turn it off any time in Notification Preferences.'
                )}
              </p>
            </div>

            <button
              type="submit"
              disabled={loading || emailStatus === 'taken' || emailStatus === 'checking'}
              className="w-full py-3 bg-purple-600 text-white font-semibold rounded-lg hover:bg-purple-700 transition-colors"
              data-testid="signup-submit-button"
            >
              {loading ? t('creatingAccount') : t('signUp')}
            </button>
          </form>

          <div className="mt-6 text-center text-sm text-gray-600">
            {t('alreadyHaveAccount')}{' '}
            <Link to="/signin" className="text-purple-600 hover:text-purple-700 font-semibold">
              {t('signIn')}
            </Link>
          </div>
        </div>
        )}
      </div>
    </div>
  );
};

export default SignUp;
